/**
 * Appels des routines (`ARCHITECTURE_V2.md` §4.1 et §4.3).
 *
 * 🔴 **Toutes les routes d'écriture sont adressées par `clientId`** (§2.3-b),
 * et une routine s'écrit **en une seule opération, arbre compris** : blocs,
 * lignes et exercices visés portent chacun leur identité cliente. Composer une
 * routine hors ligne ne demande donc ni place-titre `{{op:n}}`, ni déclaration
 * de dépendance, ni ordre de drainage — c'est la première fonction du projet à
 * encaisser vraiment le §2.3-b.
 *
 * ⚠️ **`crypto.randomUUID()` et rien d'autre**, comme pour les exercices : un
 * identifiant dérivé du nom ferait collision entre deux blocs homonymes, et un
 * compteur local ferait collision avec lui-même après une réinstallation.
 */

import type { RoutineDTO, RoutineInput, UpdateRoutineBody } from '../../../shared/types';
import { apiFetch } from './client';

/**
 * Clé de cache de la **réplique** : toutes les routines, arbre complet.
 *
 * 🔴 **Une seule entrée, sans terme de recherche dans la clé** — la leçon de
 * l'étape 4 (`lib/localSearch.ts`) : une clé par terme rend la recherche
 * impossible hors ligne, puisqu'un mot jamais tapé en ligne n'a rien à
 * restaurer.
 *
 * ⚠️ **Et l'arbre entier, contrairement aux exercices.** Ce n'est pas une
 * inconséquence mais la même décision sur un autre volume : les instructions
 * d'exercice pèsent 571 Ko, trente routines pèsent moins de 50 Ko. Les
 * répliquer entières rend **toutes** les routines consultables en salle, ce qui
 * est très exactement l'endroit où il n'y a pas de réseau.
 */
export const ROUTINES_KEY = ['routines'] as const;

export function fetchRoutines(): Promise<RoutineDTO[]> {
  return apiFetch<RoutineDTO[]>('/api/routines');
}

export function fetchRoutine(clientId: string): Promise<RoutineDTO> {
  return apiFetch<RoutineDTO>(`/api/routines/${encodeURIComponent(clientId)}`);
}

/**
 * Création **ou** remplacement — un `PUT`, parce que le client possède déjà
 * l'identité (§2.3-b). Idempotent par construction : rejouer réécrit le même
 * arbre au même endroit, sans dupliquer un bloc ni faire dériver une position.
 */
export function putRoutine(clientId: string, body: RoutineInput): Promise<RoutineDTO> {
  return apiFetch<RoutineDTO>(`/api/routines/${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Modification partielle — le favori, sans avoir à porter tout l'arbre. */
export function patchRoutine(clientId: string, body: UpdateRoutineBody): Promise<RoutineDTO> {
  return apiFetch<RoutineDTO>(`/api/routines/${encodeURIComponent(clientId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteRoutine(clientId: string): Promise<void> {
  return apiFetch<void>(`/api/routines/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
}
